import api from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/users`;

const OWNER_ROLE = "ROLE_OWNER";
const ADMIN_ROLE = "ROLE_ADMIN";

/**
 * User Service
 * Gerencia todas as operações CRUD de usuários (UserController - /users)
 * Listar/visualizar: qualquer perfil do painel (restrito à própria empresa).
 * Criar/editar/remover e acesso a todas as empresas: ROLE_ADMIN ou ROLE_OWNER.
 */
class UserService {
  /**
   * A API sempre devolve { data: [...] }, inclusive para um único registro
   * @param {object} response - Axios response
   * @returns {object|null}
   */
  extractOne(response) {
    const payload = response.data?.data ?? response.data;
    return Array.isArray(payload) ? (payload[0] ?? null) : payload;
  }

  /**
   * Get all users
   * @returns {Promise<Array>}
   */
  async getAllUsers() {
    try {
      const response = await api.get(API_BASE);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar usuários:", error);
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param {number} id - User ID
   * @returns {Promise<object>}
   */
  async getUserById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return this.extractOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar usuário ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new user
   * @param {object} userData - { email, username, password, enabled, roles }
   * @returns {Promise<object>}
   */
  async createUser(userData) {
    try {
      const response = await api.post(
        API_BASE,
        userData
      );
      return this.extractOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar usuário:", error);
      throw error;
    }
  }

  /**
   * Update user (senha e roles opcionais: se omitidas, mantém as atuais)
   * @param {number} id - User ID
   * @param {object} userData - { email, username, password?, enabled, roles? }
   * @returns {Promise<object>}
   */
  async updateUser(id, userData) {
    try {
      const response = await api.put(
        `${API_BASE}/${id}`,
        userData
      );
      return this.extractOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar usuário ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete user (o backend não permite remover o próprio usuário)
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  async deleteUser(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar usuário ${id}:`, error);
      throw error;
    }
  }

  /**
   * Normaliza nome de role para o padrão ROLE_XXX
   * @param {string} role
   * @returns {string}
   */
  normalizeRole(role) {
    const name = String(role || "")
      .trim()
      .toUpperCase();
    if (!name) return "";
    return name.startsWith("ROLE_") ? name : `ROLE_${name}`;
  }

  /**
   * Verifica se o usuário possui a role informada
   * @param {object} user - { roles: string[] } ou { role: string }
   * @param {string} role
   * @returns {boolean}
   */
  hasRole(user, role) {
    const target = this.normalizeRole(role);
    const roles = Array.isArray(user?.roles)
      ? user.roles
      : user?.role
        ? [user.role]
        : [];
    return roles.some((r) => this.normalizeRole(r) === target);
  }

  /**
   * Verifica se o usuário é ROLE_OWNER
   * @param {object} user
   * @returns {boolean}
   */
  isOwner(user) {
    return this.hasRole(user, OWNER_ROLE);
  }

  /**
   * Cadastrar, editar e remover usuários: ROLE_ADMIN (só na própria empresa)
   * e ROLE_OWNER (qualquer empresa). Os demais perfis só consultam.
   * @param {object} user - usuário logado (useAuth)
   * @returns {boolean}
   */
  canManageUsers(user) {
    return this.isOwner(user) || this.hasRole(user, ADMIN_ROLE);
  }

  /**
   * Escolher a empresa do usuário (company_code no payload) e ver usuários de
   * todas as empresas: exclusivo do ROLE_OWNER. Para os demais a API ignora
   * o campo e grava na empresa de quem está logado.
   * @param {object} user
   * @returns {boolean}
   */
  canChooseCompany(user) {
    return this.isOwner(user);
  }

  /**
   * Empresa à qual o usuário logado está restrito (claim company_code do JWT).
   * Retorna null para o ROLE_OWNER (acesso a todas as empresas).
   * Para os demais perfis sem empresa retorna "" (sem acesso a usuários).
   * @param {object} user - usuário logado (useAuth)
   * @returns {string|null}
   */
  getCallerCompanyCode(user) {
    if (this.canChooseCompany(user)) return null;
    return user?.company_code || "";
  }

  /**
   * Formata data/hora para exibição (DD/MM/YYYY)
   * @param {string} dateString
   * @returns {string}
   */
  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return Number.isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString("pt-BR");
  }

  /**
   * Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;
